<template>
  <div v-if="fetchingClient" class="flex flex-row w-10 mt-2 mr-2 gap-4">
    <Skeleton width="15rem" height="2rem" />
    <Skeleton width="2rem" height="2rem" shape="circle" />
  </div>
  <div v-else class="flex flex-row w-10 mt-2 mr-2">
    <HeadTitle
      :title="isEditMode ? 'Editar cliente' : 'Detalles del cliente'"
      description="En esta sección puedes visualizar y editar la información de un grupo de nóminas"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>

  <div v-if="fetchingClient" class="detail-form">
    <div class="input-text mr-3 gap-2">
      <Skeleton width="5rem" height="1rem" />
      <Skeleton width="15rem" height="2.5rem" />
    </div>
    <div class="input-text mr-3 gap-2">
      <Skeleton width="5rem" height="1rem" />
      <Skeleton width="15rem" height="2.5rem" />
    </div>
  </div>
  <form v-else class="detail-form" @submit="onSubmit">
    <div class="input-text mr-3">
      <label class="text-800 font-light mb-2" for="code">Código</label>
      <InputText disabled v-model="formValues.code" />
    </div>
    <div class="input-text mr-3">
      <label class="text-800 font-light mb-2" for="name">Nombre</label>
      <Field v-slot="{ field, errors }" name="name">
        <InputText
          :disabled="!isEditMode"
          v-bind="field"
          :class="{ 'p-invalid': errors.length }"
        />
        <small v-if="errors.length" class="p-error">{{ errors[0] }}</small>
      </Field>
    </div>
  </form>

  <Divider />
  <h4>Integración</h4>

  <div v-if="fetchingClient" class="detail-form">
    <div class="input-text mr-3 gap-2">
      <Skeleton width="5rem" height="1rem" />
      <Skeleton width="15rem" height="2.5rem" />
    </div>
    <div class="mr-2 flex flex-column mt-3 mr-3 gap-2">
      <Skeleton width="8rem" height="1rem" />
      <Skeleton width="15rem" height="2.5rem" />
    </div>
    <div class="mr-2 flex flex-column mt-3 mr-3 gap-2">
      <Skeleton width="8rem" height="1rem" />
      <Skeleton width="15rem" height="2.5rem" />
    </div>
  </div>
  <form v-else class="detail-form">
    <div class="input-text mr-3">
      <label class="text-800 font-bold mb-2 mt-3" for="code">Nombre</label>
      <InputText
        disabled
        :model-value="String(client?.integrationType?.name)"
      />
    </div>

    <div
      class="mr-2 flex flex-column mt-3 mr-3"
      v-for="field in integrationFields"
      :key="field.name"
    >
      <label class="text-800 font-semibold mb-2" :for="field.name">{{
        field.name
      }}</label>
      <Field
        v-slot="{ field: fieldProps, errors }"
        :name="`integrationFields.${field.name}`"
      >
        <InputText
          :disabled="!isEditMode"
          v-bind="fieldProps"
          :class="{ 'p-invalid': errors.length }"
        />
        <small v-if="errors.length" class="p-error">{{ errors[0] }}</small>
      </Field>
    </div>
  </form>
  <ConfirmDialog></ConfirmDialog>

  <Footer>
    <template #left>
      <AuditButton :record-ids="[client?.id || '']" :entities="['Client']" />
    </template>
    <div class="flex flex-row align-self-end">
      <Button
        class="text-sm font-light mr-2"
        :label="isEditModeLabel"
        severity="secondary"
        @click="router.back()"
      />
      <Button
        class="text-sm font-light"
        v-if="isEditMode"
        label="Guardar"
        @click="onSubmit"
        severity="primary"
      />
    </div>
  </Footer>
</template>
<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import { getClientById, updateClient } from '../services/clientService'
  import Client from '../models/Client'
  import router from '@/router'
  import { useForm, Field } from 'vee-validate'
  import * as yup from 'yup'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  const path = useRoute()
  const client = ref<Client>()
  const integrationFields = ref()
  const isEditMode = ref(false)
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const fetchingClient = ref(false)

  const schema = yup.object({
    code: yup.string().required('El código es requerido'),
    name: yup.string().required('El nombre es requerido'),
    integrationFields: yup
      .object()
      .test(
        'all-fields-required',
        'Todos los campos de integración son requeridos',
        (obj) => obj && Object.values(obj).every((value) => value)
      ),
  })

  const {
    handleSubmit,
    values: formValues,
    errors,
    setFieldValue,
  } = useForm({
    validationSchema: schema,
    initialValues: {
      code: '',
      name: '',
      integrationFields: {},
    },
  })

  onMounted(() => {
    const { id } = path.params
    fetchingClient.value = true
    getClientById(String(id))
      .then((data) => {
        client.value = data
        setFieldValue('code', data.code || '')
        setFieldValue('name', data.name)
        setFieldValue('integrationFields', data.integrationFields)
        integrationFields.value = Object.entries(data.integrationFields).map(
          ([fieldName, fieldValue]) => ({
            name: fieldName,
            value: fieldValue,
          })
        )
      })
      .catch((error) => {
        router.push('/clients')
      })
      .finally(() => {
        fetchingClient.value = false
      })
  })

  const isEditModeLabel = computed(() =>
    isEditMode.value ? 'Cancelar' : 'Volver'
  )

  const onSubmit = handleSubmit(async (values) => {
    if (!isEditMode.value) return

    confirm.require({
      message:
        'La modificación de los parametros puede afectar el comportamiento actual del sistema, ¿Deseas continuar?',
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        const response = await updateClient(client.value?.id || '', {
          name: values.name,
          integrationFields: values.integrationFields,
        })

        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Cliente modificado con exito!.`,
          })

          router.push('/clients')
        }
      },
      reject: () => {},
    })
  })

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }
</script>
<style scoped>
  .detail-form {
    display: flex;
    flex-flow: row wrap;
    width: 70%;
    margin-top: 2rem;
  }

  .input-text {
    display: flex;
    flex-flow: column wrap;
  }
</style>
